<script lang="ts">
  import clsx from 'clsx';
  import { onMount } from 'svelte';
  import { KeyboardArrowDown } from '@athena-lib/Icons';
  import { Link } from '@athena-lib/Link';
  import { getCurrentDevice } from '@athena-lib/device';
  import { generateUniqueElementId } from '@athena-lib/unique-element-id';
  import type { LinkCollectionsProps } from './types';

  const { linkCollections, action }: LinkCollectionsProps = $props();

  let lists: HTMLUListElement[] = $state([]);
  let openCollections: number[] = $state([]);
  const domState = new Map<HTMLUListElement, boolean | undefined>();

  function toggleCollection(pos: number) {
    if (openCollections.includes(pos)) {
      const idx = openCollections.indexOf(pos);
      openCollections.splice(idx, 1);
    } else {
      openCollections.push(pos);
    }
  }

  let isMounted = $state(false);
  onMount(() => {
    isMounted = true;
  });

  $effect(() => {
    if (!window.matchMedia('(max-width: 768px)').matches) {
      return;
    }

    for (let pos = 0; pos < lists.length; pos++) {
      if (openCollections.includes(pos)) {
        expandPanel(pos);
      } else {
        collapsePanel(pos);
      }
    }
  });

  /*
    Generally we prefer to utilize CSS to manage different layouts in
    regards to our responsive breakpoints. In this case we need to know
    which context we are rendering to alter the tab-index of the links
    since this is cheaper solution rather than duplicating our markup.
  */
  const isDesktop = $derived.by(() => {
    const device = getCurrentDevice() ?? 'sm';
    return ['md', 'lg'].includes(device);
  });

  function collapsePanel(pos: number) {
    const panel = lists[pos];
    const sectionHeight = panel.scrollHeight;

    const panelTransition = panel.style.transition;
    panel.style.transition = '';
    domState.set(panel, false);

    requestAnimationFrame(() => {
      panel.style.height = sectionHeight + 'px';
      panel.style.transition = panelTransition;
      requestAnimationFrame(() => {
        panel.style.height = '0px';
      });
    });
  }

  function expandPanel(pos: number) {
    const panel = lists[pos];
    if (domState.get(panel)) {
      return;
    }
    domState.set(panel, true);

    const sectionHeight = panel.scrollHeight;
    panel.style.height = sectionHeight + 'px';

    const transitionend = () => {
      panel.removeEventListener('transitionend', transitionend);
      panel.style.height = '';
    };
    panel.addEventListener('transitionend', transitionend);
  }

  function elementAction(node: HTMLDivElement, actionArg: unknown) {
    if (action) {
      return action(node, actionArg!);
    }
  }
</script>

{#each linkCollections as linkCollection, pos (linkCollection.heading)}
  {@const isOpen = openCollections.includes(pos) || isDesktop}
  {@const identifier = generateUniqueElementId()}
  <div
    class="mb-4 mr-gutter w-full last:mr-0 md:mb-0 md:w-auto md:basis-1/3"
    use:elementAction={linkCollection.actionArg}
  >
    <h4 class="mb-4 font-semibold" id={`${identifier}-heading`}>
      <button
        type="button"
        class="flex w-full items-center justify-between md:hidden"
        onclick={() => toggleCollection(pos)}
        aria-expanded={isOpen}
        aria-controls={`${identifier}-links`}
      >
        <span>
          {linkCollection.heading}
        </span>
        <div class="h-4 w-4">
          <KeyboardArrowDown />
        </div>
      </button>
      <span class="hidden md:block">{linkCollection.heading}</span>
    </h4>
    <ul
      id={`${identifier}-links`}
      class={clsx(
        isOpen ? 'block' : !isMounted && 'hidden',
        'h-auto overflow-hidden transition-all duration-300 md:block',
      )}
      bind:this={lists[pos]}
      aria-labelledby={`${identifier}-heading`}
      aria-hidden={!isOpen}
    >
      {#each linkCollection.links as link (link.link?.href + link.text)}
        <li class="leading-loose last:pb-8">
          <Link {...link.link} onClick={link.action} tabindex={isOpen ? 0 : -1}>{link.text}</Link>
        </li>
      {/each}
    </ul>
  </div>
{/each}
